/**
 * The c'est interdit wishlist. Saved products are a list of product handles in this browser's
 * localStorage (newest first), so a wishlist stays on the device it was made on.
 *
 * - Any [data-wishlist-toggle][data-handle] button saves / removes its product (the hearts on product
 *   cards, the product page's button); aria-pressed shows the state.
 * - [data-wishlist-count] shows how many products are saved (the header heart's bubble; the number
 *   goes in its [data-wishlist-count-text]), hidden at 0.
 * - The wishlist page's grid ([data-wishlist-results] .product-grid) is filled with the saved products' cards: each comes from
 *   /products/<handle>?view=wishlist-card, a layout-less product template that renders only the
 *   theme's own product card, so it matches the collection pages. Its bar: share (a link
 *   with ?items=…, shown as "a shared wishlist" with "Save to my wishlist") and clear; adding to
 *   cart is each card's own Quick add, where colour and size are picked.
 */

const KEY = 'cid-wishlist';

/** @returns {string[]} */
function read() {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} list */
function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Private mode or storage full: the heart still toggles for this page view.
  }
}

let list = read();

function sync() {
  for (const button of document.querySelectorAll('[data-wishlist-toggle]')) {
    const saved = list.includes(/** @type {HTMLElement} */ (button).dataset.handle || '');
    if (button.getAttribute('aria-pressed') !== String(saved)) button.setAttribute('aria-pressed', String(saved));
  }
  for (const count of document.querySelectorAll('[data-wishlist-count]')) {
    const text = count.querySelector('[data-wishlist-count-text]') || count;
    text.textContent = String(list.length);
    count.toggleAttribute('hidden', list.length === 0);
  }
}

/** @param {string} handle */
function toggle(handle) {
  const saved = list.includes(handle);
  list = saved ? list.filter((item) => item !== handle) : [handle, ...list];
  write(list);
  sync();
  return !saved;
}

// Capture phase: the heart sits on a product card, whose own click handler would open the product.
document.addEventListener(
  'click',
  (event) => {
    const button = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('[data-wishlist-toggle]')
    );
    if (!button?.dataset.handle) return;
    event.preventDefault();
    // Immediate: no other click handler (theme or app) may act on a wishlist click as well.
    event.stopImmediatePropagation();
    const saved = toggle(button.dataset.handle);
    // A beat out when saved, a beat in when removed.
    const beat = saved ? 'data-pop' : 'data-unpop';
    button.removeAttribute('data-pop');
    button.removeAttribute('data-unpop');
    button.setAttribute(beat, '');
    setTimeout(() => button.removeAttribute(beat), 450);
    if (saved) keepInGrid(button.dataset.handle);
    else removeFromGrid(button.dataset.handle);
  },
  true
);

// In a carousel (homepage product lists), a press on the bookmark would start the slideshow's
// mouse drag: the slightest movement then counts as a drag and the slideshow cancels the click.
// The slideshow never sees a press that starts on a wishlist button.
document.addEventListener(
  'mousedown',
  (event) => {
    if (/** @type {HTMLElement} */ (event.target).closest?.('[data-wishlist-toggle]')) event.stopPropagation();
  },
  true
);

// Another tab changed the list.
window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  list = read();
  sync();
});

// Cards arrive later too (infinite scroll, quick add, search, the wishlist page itself).
let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    sync();
  });
}).observe(document.documentElement, { childList: true, subtree: true });

sync();

/* The wishlist page */

const grid = /** @type {HTMLElement | null} */ (document.querySelector('[data-wishlist-results] .product-grid'));
const bar = /** @type {HTMLElement | null} */ (document.querySelector('[data-wishlist-bar]'));
const status = /** @type {HTMLElement | null} */ (document.querySelector('[data-wishlist-status]'));

// A shared link (?items=a,b) shows that list instead of this browser's own.
const sharedParam = new URL(window.location.href).searchParams.get('items');
const shared = sharedParam ? sharedParam.split(',').filter(Boolean) : null;

/** Cards on their way out, by handle: saving the product again before it's gone keeps the card. */
/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const leaving = new Map();

/**
 * The bookmark empties, then the card fades out (CSS on [data-removing], sections/wishlist.liquid)
 * and the cards after it slide into its place.
 * @param {string} handle
 */
function removeFromGrid(handle) {
  if (!grid || shared) return;
  const item = /** @type {HTMLElement | null} */ (grid.querySelector(`[data-wishlist-item="${CSS.escape(handle)}"]`));
  if (!item) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    item.remove();
    showEmpty();
    return;
  }
  item.setAttribute('data-removing', '');
  leaving.set(
    handle,
    setTimeout(() => {
      leaving.delete(handle);
      const others = [...grid.querySelectorAll('[data-wishlist-item]')].filter((other) => other !== item);
      const before = new Map(others.map((other) => [other, other.getBoundingClientRect()]));
      item.remove();
      for (const other of others) {
        const from = before.get(other);
        const to = other.getBoundingClientRect();
        if (!from || (from.left === to.left && from.top === to.top)) continue;
        other.animate(
          [{ transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)` }, { transform: 'none' }],
          { duration: 350, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
        );
      }
      showEmpty();
    }, 600)
  );
}

/** @param {string} handle */
function keepInGrid(handle) {
  const timer = leaving.get(handle);
  if (timer === undefined) return;
  clearTimeout(timer);
  leaving.delete(handle);
  grid?.querySelector(`[data-wishlist-item="${CSS.escape(handle)}"]`)?.removeAttribute('data-removing');
}

function showEmpty() {
  const empty = document.querySelector('[data-wishlist-empty]');
  const count = grid?.querySelectorAll('[data-wishlist-item]').length || 0;
  empty?.toggleAttribute('hidden', count > 0);
  grid?.closest('[data-wishlist-results]')?.toggleAttribute('hidden', count === 0);
  bar?.toggleAttribute('hidden', count === 0);
  for (const total of document.querySelectorAll('[data-wishlist-total]')) total.textContent = String(count);
  for (const element of document.querySelectorAll('[data-wishlist-mode]')) {
    element.toggleAttribute('hidden', /** @type {HTMLElement} */ (element).dataset.wishlistMode !== (shared ? 'shared' : 'own'));
  }
}

/**
 * @param {string} key - The status template's data attribute (copied).
 * @param {Record<string, string>} [values]
 */
function message(key, values = {}) {
  const template = status?.dataset[key] || '';
  return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, value), template);
}

async function renderGrid() {
  if (!grid) return;
  const handles = shared || list;
  const root = window.Shopify?.routes?.root || '/';
  const cards = await Promise.all(
    handles.map(async (handle) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=wishlist-card`);
        if (!response.ok) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return html.querySelector('[data-wishlist-item]');
      } catch {
        return null;
      }
    })
  );
  // Products that no longer exist drop off this browser's list.
  if (!shared) {
    const gone = list.filter((_, index) => !cards[index]);
    if (gone.length) {
      list = list.filter((handle) => !gone.includes(handle));
      write(list);
    }
  }
  grid.replaceChildren(...cards.filter((card) => card !== null).map((card) => document.importNode(card, true)));
  grid.removeAttribute('aria-busy');
  showEmpty();
  sync();
}

/** @type {ReturnType<typeof setTimeout> | undefined} */
let flashTimer;

/**
 * Shows a short message beside Share, then fades it out.
 * @param {string} text
 */
function flash(text) {
  if (!status) return;
  clearTimeout(flashTimer);
  status.removeAttribute('data-fading');
  status.textContent = text;
  flashTimer = setTimeout(() => {
    status.setAttribute('data-fading', '');
    flashTimer = setTimeout(() => {
      status.textContent = '';
      status.removeAttribute('data-fading');
    }, 300);
  }, 2500);
}

/** @param {HTMLElement} button */
async function share(button) {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('items', list.join(','));
  try {
    if (navigator.share && window.matchMedia('(hover: none)').matches) {
      await navigator.share({ title: document.title, url: url.toString() });
      return;
    }
    await navigator.clipboard.writeText(url.toString());
    flash(message('copied'));
  } catch {
    // Share sheet dismissed or clipboard blocked.
  }
  button.blur();
}

/** First click asks ("Clear all?"), a second one within 4 seconds clears. */
/** @param {HTMLElement} button */
function clear(button) {
  if (!button.hasAttribute('data-confirming')) {
    button.dataset.label = button.textContent || '';
    button.textContent = button.dataset.confirmLabel || '';
    button.setAttribute('data-confirming', '');
    setTimeout(() => {
      if (!button.hasAttribute('data-confirming')) return;
      button.removeAttribute('data-confirming');
      button.textContent = button.dataset.label || '';
    }, 4000);
    return;
  }
  button.removeAttribute('data-confirming');
  button.textContent = button.dataset.label || '';
  list = [];
  write(list);
  grid?.replaceChildren();
  if (status) status.textContent = '';
  showEmpty();
  sync();
}

/** A shared list: its products join this browser's list, then the page shows your own list. */
function saveShared() {
  if (!shared) return;
  list = [...shared.filter((handle) => !list.includes(handle)), ...list];
  write(list);
  window.location.href = window.location.pathname;
}

bar?.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const shareButton = /** @type {HTMLElement | null} */ (target.closest('[data-wishlist-share]'));
  if (shareButton) return share(shareButton);
  const clearButton = /** @type {HTMLElement | null} */ (target.closest('[data-wishlist-clear]'));
  if (clearButton) return clear(clearButton);
  if (target.closest('[data-wishlist-save-shared]')) saveShared();
});

renderGrid();
